import React, { useEffect, useMemo, useState } from 'react';

// MUI (Mantis)
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import { WhatsAppOutlined } from '@ant-design/icons';

import api from '../../api/axios';

// ===== AUTH: "apiKey" | "cookie" | "none"
const AUTH_MODE = 'apiKey';
const getAuthCfg = () => {
  switch (AUTH_MODE) {
    case 'apiKey':
      return { withCredentials: true, headers: { 'X-API-Key': 'secret123' } };
    case 'cookie':
      return { withCredentials: true };
    default:
      return {};
  }
};

function StatusChip({ value }) {
  return <Chip size="small" label={value ? 'Connected' : 'Disconnected'} color={value ? 'success' : 'error'} />;
}

/**
 * Convert:
 *  - "16462958823@c.us" -> "+1 646 295 8823"
 *  - "6281990906119@c.us" -> "+62 819 9090 6119"
 *  - "639xxxxxxxxx@c.us" -> "+63 ..."
 */
const stripWaSuffix = (s) =>
  String(s || '')
    .replace(/@c\.us$/i, '')
    .replace(/@s\.whatsapp\.net$/i, '');

const guessCountryCode = (digits) => {
  if (digits.startsWith('62')) return '62'; // Indonesia
  if (digits.startsWith('63')) return '63'; // Philippines
  if (digits.startsWith('1')) return '1'; // US/CA
  return '';
};

const formatWithSpaces = (countryCode, nationalNumber) => {
  if (!countryCode) return nationalNumber;

  if (countryCode === '1') {
    const n = nationalNumber;
    if (n.length === 10) return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
    return n;
  }

  const n = nationalNumber;
  if (n.length >= 10) return `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`;
  if (n.length >= 7) return `${n.slice(0, 3)} ${n.slice(3)}`;
  return n;
};

const formatWaNumber = (waId) => {
  if (!waId) return '-';
  const raw = stripWaSuffix(waId);
  const digits = raw.replace(/\D/g, '');
  if (!digits) return waId;

  const cc = guessCountryCode(digits);
  if (!cc) return raw;

  const national = digits.slice(cc.length);
  const pretty = formatWithSpaces(cc, national);
  return `+${cc} ${pretty}`.trim();
};

// --- QR renderer helper (supports qrDataUrl OR plain qr string)
function QRBlock({ qrText, qrDataUrl }) {
  const src = qrDataUrl || (typeof qrText === 'string' && qrText.startsWith('data:image/') ? qrText : '');
  const showImage = !!src;

  if (!qrText && !qrDataUrl) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        QR
      </Typography>

      {showImage ? (
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.02)'
          }}
        >
          <Box
            component="img"
            src={src}
            alt="QR"
            sx={{
              width: 260,
              height: 260,
              objectFit: 'contain',
              borderRadius: 1,
              background: '#fff',
              p: 1
            }}
          />
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 1 }}>
            QR received. If it does not render as an image, copy the text below (backend format may not be a data-url).
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={qrText}
            InputProps={{ readOnly: true }}
            sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
          />
        </>
      )}
    </Box>
  );
}

export default function Account() {
  const AUTH = useMemo(getAuthCfg, []);

  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination (server: 1-based)
  const [perPage, setPerPage] = useState(25);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // search (auto-hit)
  const [query, setQuery] = useState(''); // typed sessionName

  // selection (bulk delete) -> set of sessionName
  const [selected, setSelected] = useState(() => new Set());
  const currentPageSessionNames = useMemo(() => rows.map((r) => r.session_name).filter(Boolean), [rows]);
  const allChecked = currentPageSessionNames.length > 0 && currentPageSessionNames.every((n) => selected.has(n));
  const someChecked = !allChecked && currentPageSessionNames.some((n) => selected.has(n)) && currentPageSessionNames.length > 0;

  // confirm dialog (delete selected)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // confirm prune
  const [pruneOpen, setPruneOpen] = useState(false);
  const [pruning, setPruning] = useState(false);

  // ===== Create Session modal =====
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  // success modal after create
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ===== QR states (global, per "active" session for QR) =====
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrSessionName, setQrSessionName] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrText, setQrText] = useState(''); // legacy plain qr string
  const [qrDataUrl, setQrDataUrl] = useState(''); // preferred: data:image/png;base64,...
  const [qrGenerated, setQrGenerated] = useState(false);
  const [qrActive, setQrActive] = useState(false); // true kalau session.active === true

  // ✅ cooldown before QR can be generated (10s)
  const [qrReadyIn, setQrReadyIn] = useState(0); // seconds left
  const qrReady = qrReadyIn <= 0;

  // countdown effect
  useEffect(() => {
    if (qrReadyIn <= 0) return;

    const t = setInterval(() => {
      setQrReadyIn((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);

    return () => clearInterval(t);
  }, [qrReadyIn]);

  const buildPath = () => `/api/sessions/all/${perPage}/${pageIndex}`;

  const mapSessionToRow = (s) => ({
    session_name: s.sessionName,
    number: s.number,
    session_id: s.sessionId,
    wa_name: s.waName,
    wa_id: s.waId,
    device_platform: s?.device?.platform || '-',
    device_pushname: s?.device?.pushname || '-',
    active: !!s?.active,
    live: !!s?.live
  });

  const fetchSessionsAll = async () => {
    setLoading(true);
    try {
      const res = await api.get(buildPath(), AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.sessions) ? payload.sessions : [];

      setRows(list.map(mapSessionToRow));

      const limit = Number(payload.limit) || perPage;
      const total = Number(payload.total) || 0;

      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
      setSelected(new Set());
    } catch (err) {
      console.error('fetch sessions error:', err);
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  // Search by sessionName using GET /api/sessions/:sessionName
  const fetchBySessionName = async (name) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/sessions/${encodeURIComponent(name)}`, AUTH);
      const d = res?.data;

      if (d && d.sessionName) {
        setRows([mapSessionToRow(d)]);
        setTotalCount(1);
        setTotalPages(1);
        setSelected(new Set());
      } else {
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        setSelected(new Set());
      }
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  // AUTO-HIT search with debounce
  useEffect(() => {
    const q = query.trim();
    const t = setTimeout(() => {
      setPageIndex(1);
      if (q) fetchBySessionName(q);
      else fetchSessionsAll();
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Normal pagination fetch (only when NOT searching)
  useEffect(() => {
    if (query.trim()) return;
    fetchSessionsAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, pageIndex]);

  const clearSearch = () => {
    setQuery('');
    setPageIndex(1);
  };

  const toggleRow = (sessionName) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sessionName)) next.delete(sessionName);
      else next.add(sessionName);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) currentPageSessionNames.forEach((n) => next.delete(n));
      else currentPageSessionNames.forEach((n) => next.add(n));
      return next;
    });
  };

  // ✅ BULK DELETE:
  // DELETE /api/sessions
  // body: { arr: [{sessionName:'QR-1'},{sessionName:'QR-2'}] }
  const deleteSessions = async (sessionNames) => {
    if (!sessionNames?.length) return;
    setDeleting(true);
    try {
      const payload = { arr: sessionNames.map((n) => ({ sessionName: n })) };
      await api.delete('/api/sessions', { ...AUTH, data: payload });

      const q = query.trim();
      if (q) await fetchBySessionName(q);
      else await fetchSessionsAll();
    } catch (err) {
      console.error('bulk delete error:', err);
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
      setSelected(new Set());
    }
  };

  // DELETE /api/sessions/prune
  const pruneInactive = async () => {
    setPruning(true);
    try {
      await api.delete('/api/sessions/prune', AUTH);
      clearSearch();
      await fetchSessionsAll();
    } finally {
      setPruning(false);
      setPruneOpen(false);
    }
  };

  // ===== Create session flow (tanpa QR) =====
  const openCreate = () => {
    setCreateOpen(true);
    setCreateName('');
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const handleCreateSession = async () => {
    const name = createName.trim();
    if (!name) return;

    setCreating(true);
    try {
      const res = await api.post(`/api/sessions`, { sessionName: name }, AUTH);
      const msg = res?.data?.message || 'Session created';
      const sName = res?.data?.sessionName || name;

      setSuccessMessage(msg || `Session "${sName}" created.`);
      setSuccessOpen(true);
      setCreateOpen(false);
      setCreateName('');

      const q = query.trim();
      if (q) await fetchBySessionName(q);
      else await fetchSessionsAll();
    } catch (err) {
      setSuccessMessage(err?.response?.data?.message || err?.message || 'Failed to create session');
      setSuccessOpen(true);
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  // ===== QR flow di samping ACTIONS =====
  const openQrForSession = (sessionName, isActive) => {
    setQrSessionName(sessionName);
    setQrText('');
    setQrDataUrl('');
    setQrGenerated(false);
    setQrActive(!!isActive); // simpan status active session
    setQrReadyIn(isActive ? 0 : 10); // countdown hanya kalau belum active
    setQrDialogOpen(true);
  };

  const closeQrDialog = async () => {
    if (qrLoading) return;
    setQrDialogOpen(false);
    setQrSessionName('');
    setQrText('');
    setQrDataUrl('');
    setQrGenerated(false);
    setQrReadyIn(0);
    setQrActive(false);

    // refresh table setelah modal QR ditutup
    const q = query.trim();
    if (q) await fetchBySessionName(q);
    else await fetchSessionsAll();
  };

  const handleGenerateQR = async () => {
    if (!qrSessionName) return;
    if (!qrReady) return;
    if (qrActive) return; // safety: kalau active, jangan generate

    setQrLoading(true);
    try {
      const res = await api.get(`/api/sessions/${encodeURIComponent(qrSessionName)}/qr`, AUTH);
      const data = res?.data || {};
      setQrText(data.qr || '');
      setQrDataUrl(data.qrDataUrl || '');
      setQrGenerated(true);
    } catch (err) {
      console.error('get qr error:', err);
      setQrText(err?.response?.data?.message || err?.message || 'Failed to get QR');
      setQrDataUrl('');
      setQrGenerated(false);
    } finally {
      setQrLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Sessions"
          action={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button variant="contained" size="small" startIcon={<WhatsAppOutlined />} onClick={openCreate}>
                Create Session
              </Button>

              <Button size="small" color="error" variant="outlined" disabled={pruning || deleting} onClick={() => setPruneOpen(true)}>
                Delete All Inactive
              </Button>

              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={selected.size === 0 || deleting}
                onClick={() => setConfirmOpen(true)}
                startIcon={<DeleteOutlined />}
              >
                Delete Selected ({selected.size})
              </Button>

              <TextField
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by sessionName"
                sx={{ width: { xs: 240, md: 360 } }}
                InputProps={{
                  endAdornment: (
                    <Stack direction="row" spacing={0.5} sx={{ mr: 0.5 }}>
                      {!!query ? (
                        <Tooltip title="Clear">
                          <IconButton size="small" onClick={clearSearch}>
                            <CloseOutlined />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  )
                }}
              />
            </Stack>
          }
        />

        <CardContent>
          {/* Table */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', width: 48 }}>
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked}
                      onChange={toggleAll}
                      disabled={currentPageSessionNames.length === 0 || deleting || pruning}
                    />
                  </Box>

                  {['SESSION NAME', 'NUMBER', 'WA NAME', 'WA ID', 'ACTIVE', 'LIVE', 'PLATFORM', 'PUSHNAME'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'left',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {h}
                    </Box>
                  ))}

                  <Box
                    component="th"
                    sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    ACTIONS
                  </Box>
                </Box>
              </Box>

              <Box component="tbody">
                {rows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={10} sx={{ p: 1, opacity: 0.8 }}>
                      {loading ? 'Loading...' : 'No sessions'}
                    </Box>
                  </Box>
                ) : (
                  rows.map((d) => {
                    const name = d.session_name;
                    const isQrLoadingForRow = qrLoading && qrSessionName === name;

                    return (
                      <Box component="tr" key={name}>
                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Checkbox checked={selected.has(name)} disabled={deleting || pruning} onChange={() => toggleRow(name)} />
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {d.session_name}
                          </Typography>
                        </Box>

                        {/* NUMBER formatted */}
                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.9 }}>
                            {formatWaNumber(d.number)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.55, fontFamily: 'monospace' }}>
                            {d.number || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.wa_name || '-'}
                        </Box>

                        {/* WA ID formatted */}
                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.9 }}>
                            {formatWaNumber(d.wa_id)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.55, fontFamily: 'monospace' }}>
                            {d.wa_id || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <StatusChip value={d.active} />
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <StatusChip value={d.live} />
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.device_platform || '-'}
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.device_pushname || '-'}
                        </Box>

                        <Box
                          component="td"
                          sx={{
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'right',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip
                              title={d.active ? 'Session sudah active, QR tidak bisa digenerate lagi' : 'Buka QR untuk scan / generate'}
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<WhatsAppOutlined />}
                                  disabled={isQrLoadingForRow}
                                  onClick={() => openQrForSession(name, d.active)}
                                >
                                  QR
                                </Button>
                              </span>
                            </Tooltip>

                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={deleting || pruning}
                              onClick={() => {
                                setSelected(new Set([name]));
                                setConfirmOpen(true);
                              }}
                              startIcon={<DeleteOutlined />}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Pagination (disabled when searching) */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Total: {totalCount} • Page {pageIndex} / {totalPages}
              {query.trim() ? ` • Search: ${query.trim()}` : ''}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                disabled={pageIndex <= 1 || totalCount === 0 || !!query.trim()}
              >
                Prev
              </Button>
              <Button
                size="small"
                onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
                disabled={pageIndex >= totalPages || totalCount === 0 || !!query.trim()}
              >
                Next
              </Button>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPageIndex(1);
                  }}
                  disabled={!!query.trim()}
                >
                  {[10, 25, 50, 100, 500].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} / page
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Confirm Delete Selected */}
      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Sessions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {selected.size === 0 ? 'Tidak ada session yang dipilih.' : `Hapus ${selected.size} session? Aksi ini tidak bisa dibatalkan.`}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
            Endpoint: <b>DELETE /api/sessions</b> body <b>{`{ arr: [{sessionName:"..."}] }`}</b>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={selected.size === 0 || deleting}
            onClick={() => deleteSessions(Array.from(selected))}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Prune */}
      <Dialog open={pruneOpen} onClose={() => !pruning && setPruneOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete All Inactive Sessions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Hapus semua session inactive (prune)? Aksi ini tidak bisa dibatalkan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPruneOpen(false)} disabled={pruning}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={pruning} onClick={pruneInactive}>
            {pruning ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Session Modal (tanpa QR) */}
      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create Session</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Session Name"
              placeholder="e.g. wa-1"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              disabled={creating}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateSession()}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={creating}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<WhatsAppOutlined />}
            disabled={!createName.trim() || creating}
            onClick={handleCreateSession}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal after Create */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Info</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{successMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* QR Modal per Session */}
      <Dialog open={qrDialogOpen} onClose={closeQrDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Session QR</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              Session: <b>{qrSessionName}</b>
            </Typography>

            {qrActive && (
              <Alert severity="info">
                Session ini sudah <b>active</b>, generate QR dinonaktifkan.
              </Alert>
            )}

            {!qrActive && qrReadyIn > 0 && !qrGenerated && (
              <Alert severity="info">
                Preparing QR... please wait <b>{qrReadyIn}s</b> before generating.
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <Button variant="outlined" disabled={!qrReady || qrLoading || qrActive} onClick={handleGenerateQR}>
                {qrLoading ? 'Loading QR...' : qrActive ? 'Already Active' : qrGenerated ? 'Regenerate QR' : 'Generate QR'}
              </Button>
            </Stack>

            {(qrText || qrDataUrl) && <QRBlock qrText={qrText} qrDataUrl={qrDataUrl} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQrDialog} disabled={qrLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
